import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { GET_SCHEDULED_QOTD } from "../../../graphql/IslamicQuotes/query";
import { DELETE_SCHEDULED_QOTD } from "../../../graphql/IslamicQuotes/mutation";
import {
  CATEGORY_LABELS,
  QuoteOfTheDaySchedule,
} from "../../../graphql/IslamicQuotes/types";

// Local-time YYYY-MM-DD, offset by `addDays`.
const localDateStr = (addDays = 0): string => {
  const d = new Date();
  d.setDate(d.getDate() + addDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Friendly "Mon, 3 Sep 2026" from a YYYY-MM-DD string (parsed as local).
const prettyDate = (ymd: string): string => {
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

type Props = {
  open: boolean;
  onClose: () => void;
  onChanged?: () => void; // notify parent (e.g. for a snackbar) after a delete
};

const QotdScheduleManager: React.FC<Props> = ({ open, onClose, onChanged }) => {
  // Show today → +1 year of scheduled broadcasts.
  const range = useMemo(
    () => ({ startDate: localDateStr(0), endDate: localDateStr(365) }),
    []
  );
  const [error, setError] = useState<string | null>(null);
  const [deletingDate, setDeletingDate] = useState<string | null>(null);

  const { data, loading } = useQuery(GET_SCHEDULED_QOTD, {
    variables: range,
    skip: !open,
    fetchPolicy: "cache-and-network",
  });

  const schedules: QuoteOfTheDaySchedule[] =
    data?.getScheduledQuotesOfTheDay || [];

  const [deleteQotd] = useMutation(DELETE_SCHEDULED_QOTD);

  const handleDelete = async (date: string) => {
    setDeletingDate(date);
    setError(null);
    try {
      await deleteQotd({
        variables: { date },
        // Refetch every active GetScheduledQuotesOfTheDay query (this list AND
        //   the library page's card map) so scheduled badges update everywhere.
        refetchQueries: ["GetScheduledQuotesOfTheDay"],
        awaitRefetchQueries: true,
      });
      onChanged?.();
    } catch (err: any) {
      setError(
        err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          "Failed to remove the scheduled quote."
      );
    } finally {
      setDeletingDate(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <div className="QV_dialogHeader">
        <Stack direction="row" alignItems="center" gap={1}>
          <StarIcon sx={{ color: "primary.main" }} />
          <Typography variant="h6" fontWeight={700}>
            Scheduled Quote of the Day
          </Typography>
        </Stack>
        <IconButton aria-label="close" size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent dividers sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ borderRadius: 0 }}>
            {error}
          </Alert>
        )}

        {loading && schedules.length === 0 ? (
          <Stack alignItems="center" sx={{ p: 4 }}>
            <CircularProgress size={22} />
          </Stack>
        ) : schedules.length === 0 ? (
          <div className="QV_emptyState" style={{ padding: 32 }}>
            <EventBusyIcon sx={{ fontSize: 40, color: "#d1d5db" }} />
            <Typography variant="body2" color="text.disabled">
              No upcoming Quote of the Day scheduled. Use the calendar icon on any
              library quote to schedule one.
            </Typography>
          </div>
        ) : (
          <Stack divider={<div style={{ borderTop: "1px solid #f1f1f4" }} />}>
            {schedules.map((s) => (
              <Stack
                key={s.id || s.date}
                direction="row"
                alignItems="center"
                gap={1.5}
                sx={{ p: "12px 20px" }}
              >
                <Stack sx={{ minWidth: 132 }}>
                  <Typography variant="body2" fontWeight={700} color="#1e1b4b">
                    {prettyDate(s.date)}
                  </Typography>
                  {s.quote && (
                    <Chip
                      label={
                        CATEGORY_LABELS[s.quote.category] || s.quote.category
                      }
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: 10,
                        mt: 0.5,
                        alignSelf: "flex-start",
                      }}
                    />
                  )}
                </Stack>
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    title={s.quote?.reference || undefined}
                  >
                    {s.quote?.reference || s.quoteId}
                  </Typography>
                  {s.quote?.translation && (
                    <Typography variant="caption" color="text.disabled" noWrap>
                      {s.quote.translation}
                    </Typography>
                  )}
                </Stack>
                <Tooltip title="Remove from schedule">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={deletingDate === s.date}
                      onClick={() => handleDelete(s.date)}
                    >
                      {deletingDate === s.date ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <DeleteOutlineIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QotdScheduleManager;
